import { useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useMediaRecorder } from '../components/assignments/hooks/useMediaRecorder';
import { generateMediaUploadUrl, uploadMediaToUrl } from '../api/studentAssignmentApis';
import { useAssignmentContext } from '../components/assignments/context/AssignmentContext';
import { clearMediaBlob, getMediaBlob, setMediaBlob } from '../utils/mediaSessionCache';

/** @deprecated Prefer getMediaBlob / setMediaBlob from mediaSessionCache */
export const videoBlobCache = {
  get: (questionId) => getMediaBlob({ questionId }),
  set: (questionId, blob) => setMediaBlob({ questionId, blob }),
  delete: (questionId) => clearMediaBlob({ questionId })
};

export const useVideoRecorder = ({
  id,
  maxDuration = 180,
  maxFileSize = 50,
  studentAssignmentId,
  token,
  updateAndSaveAnswer,
  pauseStrictMode,
  resumeStrictMode,
  uploadedFileKey
}) => {
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const lastBlobRef = useRef(null);
  const liveVideoRef = useRef(null);

  const { setIsMediaBusy, registerStopHandler, deregisterStopHandler } = useAssignmentContext();

  // Ref mirror of isRecording so the stop handler closure always reads the latest value.
  const isRecordingRef = useRef(false);

  // A promise created when recording starts that resolves when the upload finishes.
  // stopActiveRecording() in AssignmentContext awaits this via the registered stop handler.
  const busyPromiseRef = useRef(null);
  const busyResolveRef = useRef(null);

  const {
    isRecording,
    mediaBlob,
    duration,
    startError,
    startRecording,
    stopRecording,
    resetRecording,
    stream
  } = useMediaRecorder({ mediaType: 'video', maxDurationSeconds: maxDuration });

  // Hands the question back to the student: re-enables Save and Next, resumes strict mode
  // and unblocks anything awaiting the recording via the registered stop handler.
  const releaseBusyState = () => {
    setIsMediaBusy(false);
    if (resumeStrictMode) resumeStrictMode();
    if (busyResolveRef.current) {
      busyResolveRef.current();
      busyResolveRef.current = null;
    }
    busyPromiseRef.current = null;
  };

  // Attach the live stream to the preview video element while recording.
  useEffect(() => {
    if (stream && liveVideoRef.current && isRecording) {
      liveVideoRef.current.srcObject = stream;
    }
  }, [stream, isRecording]);

  // Keep the ref in sync with the isRecording state so the stop handler can check it safely.
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Register the stop handler once. It is called by stopActiveRecording() (in context)
  // when the assignment needs to end while a recording/upload is in progress.
  useEffect(() => {
    registerStopHandler(async () => {
      // Stop the recording if it is still running; this triggers onstop → setMediaBlob
      // → handleUpload via the mediaBlob useEffect below.
      if (isRecordingRef.current) {
        stopRecording();
      }
      // Await the upload promise (created in handleStartRecording).
      if (busyPromiseRef.current) {
        await busyPromiseRef.current;
      }
    });
    // stopRecording is stable (useCallback with no deps in useMediaRecorder).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerStopHandler, stopRecording]);

  // Trigger upload whenever a new mediaBlob is produced.
  useEffect(() => {
    if (mediaBlob && !isUploading) {
      handleUpload(mediaBlob);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaBlob]);

  const handleStartRecording = async () => {
    // Create the promise that will resolve when the subsequent upload finishes.
    // The stop handler awaits this, keeping stopActiveRecording() suspended until done.
    busyPromiseRef.current = new Promise((resolve) => {
      busyResolveRef.current = resolve;
    });
    setIsMediaBusy(true);
    setUploadError(null);
    if (pauseStrictMode) pauseStrictMode();

    const started = await startRecording();

    // Permission denied, no camera, device in use — nothing will ever be recorded or
    // uploaded, so the question must not stay stuck on "Uploading…".
    if (!started) releaseBusyState();
  };

  const handleStopRecording = () => {
    // Keep strict mode paused through upload; resume after upload completes.
    stopRecording();
  };

  const handleUpload = async (blob) => {
    if (!blob) return;

    const sizeInMB = blob.size / (1024 * 1024);
    lastBlobRef.current = blob;

    try {
      setIsUploading(true);
      setUploadError(null);
      setIsMediaBusy(true);

      // The signed URL is generated from the extension as `video/<ext>`, and R2 rejects the
      // PUT unless the Content-Type header matches it exactly — so drop any codecs suffix.
      const baseMimeType = (blob.type || 'video/mp4').split(';')[0];
      const fileExtension = baseMimeType.split('/')[1] || 'mp4';

      // 1. Get signed URL
      const { uploadUrl, fileKey } = await generateMediaUploadUrl({
        studentAssignmentId,
        questionId: id,
        mediaType: 'video',
        fileExtension,
        token
      });

      // 2. Upload to R2 directly
      await uploadMediaToUrl({ uploadUrl, file: blob, contentType: baseMimeType });

      setMediaBlob({ fileKey, questionId: id, blob });

      // 3. Save metadata to DB — keep busy until this finishes so Save and Next stays disabled
      const fileData = {
        fileKey,
        originalFileName: `video-recording.${fileExtension}`,
        sizeInMB: sizeInMB.toFixed(2),
        uploadDate: new Date(),
        duration,
        mediaType: 'video'
      };

      // onSave() bails out silently when offline, which would leave the recording in R2 but
      // absent from the response — surface it as a failure instead of a false success.
      if (!navigator.onLine) {
        throw new Error('You appear to be offline. Reconnect and try again.');
      }

      await Promise.resolve(updateAndSaveAnswer(id, fileData));

      toast({
        title: 'Success',
        description: 'Video uploaded successfully',
        status: 'success',
        duration: 2000
      });
    } catch (error) {
      console.error('upload error: ', error);
      const description = navigator.onLine
        ? error?.response?.data?.message || error?.message || 'Failed to upload video recording'
        : 'You appear to be offline. Reconnect and try again.';
      setUploadError(description);
      toast({
        title: 'Upload failed',
        description,
        status: 'error',
        duration: 5000
      });
    } finally {
      setIsUploading(false);
      releaseBusyState();
      deregisterStopHandler();
    }
  };

  const handleRetake = () => {
    clearMediaBlob({ fileKey: uploadedFileKey, questionId: id });
    lastBlobRef.current = null;
    setUploadError(null);
    resetRecording();
    updateAndSaveAnswer(id, null);
  };

  // Re-runs the upload for a recording that is still held in memory after a failure,
  // so the student does not have to record again.
  const retryUpload = () => {
    const blob = lastBlobRef.current || mediaBlob;
    if (blob && !isUploading) handleUpload(blob);
  };

  return {
    isRecording,
    isUploading,
    uploadError,
    startError,
    mediaBlob,
    duration,
    startRecording: handleStartRecording,
    stopRecording: handleStopRecording,
    resetRecording,
    retryUpload,
    handleRetake,
    liveVideoRef
  };
};
